import React, { useState } from 'react';
import { ProviderStatusCard } from './ProviderStatusCard';
import { CostSummaryCards } from './CostSummaryCards';
import { DailySpendChart } from './DailySpendChart';
import { SessionCostTable } from './SessionCostTable';
import { useMTDSpend, useDailySpend, useSessionCosts } from '../hooks/useApiUsageStats';
import { useProviderStatus } from '../hooks/useProviderStatus';

const PAGE_SIZE = 20;

export const ApiCostsDashboard: React.FC = () => {
  const [page, setPage] = useState(0);
  const [providerFilter, setProviderFilter] = useState<string | null>(null);

  const mtd = useMTDSpend();
  const daily = useDailySpend(30);
  const providerStatus = useProviderStatus();
  const sessionCosts = useSessionCosts(page, PAGE_SIZE, {
    provider: providerFilter as 'openai' | 'assemblyai' | null,
  });

  const openaiStatus = providerStatus.data?.find((p) => p.provider === 'openai');
  const assemblyaiStatus = providerStatus.data?.find(
    (p) => p.provider === 'assemblyai',
  );

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Section 1: Status + Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ProviderStatusCard
          provider="openai"
          isValid={openaiStatus?.is_valid}
          lastCheckedAt={openaiStatus?.last_checked_at ?? null}
          apiKeyHint={openaiStatus?.api_key_hint ?? null}
          isLoading={providerStatus.isLoading}
        />
        <ProviderStatusCard
          provider="assemblyai"
          isValid={assemblyaiStatus?.is_valid}
          lastCheckedAt={assemblyaiStatus?.last_checked_at ?? null}
          apiKeyHint={assemblyaiStatus?.api_key_hint ?? null}
          isLoading={providerStatus.isLoading}
        />
        <CostSummaryCards
          total={mtd.data?.total ?? 0}
          dailyAverage={mtd.data?.dailyAverage ?? 0}
          byProvider={mtd.data?.byProvider ?? { openai: 0, assemblyai: 0 }}
          callCount={mtd.data?.callCount ?? { openai: 0, assemblyai: 0 }}
          isLoading={mtd.isLoading}
        />
      </div>

      {/* Section 2: Daily Spend Chart */}
      <DailySpendChart data={daily.data ?? []} isLoading={daily.isLoading} />

      {/* Section 3: Session Cost Table */}
      <SessionCostTable
        rows={sessionCosts.data?.rows ?? []}
        total={sessionCosts.data?.total ?? 0}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        providerFilter={providerFilter}
        onProviderFilterChange={(p) => {
          setProviderFilter(p);
          setPage(0);
        }}
        isLoading={sessionCosts.isLoading}
      />
    </div>
  );
};

export default ApiCostsDashboard;
